import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';

import moment from 'moment';
import { finalize } from 'rxjs';

import { Jira, Messenger } from '../service';

export interface TimeExportData {
  id: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    FormField,
  ],
  template: `
    <h2 mat-dialog-title>Export time reporting</h2>

    <mat-dialog-content>
      <div class="entity-form">
        <mat-form-field>
          <mat-label>Enter a date range</mat-label>
          <mat-date-range-input [rangePicker]="agreementRangePicker">
            <input matStartDate required placeholder="Start date" [formField]="form.StartDate" />
            <input matEndDate required placeholder="End date" [formField]="form.EndDate" />
          </mat-date-range-input>
          <mat-hint>DD/MM/YYYY – DD/MM/YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="agreementRangePicker"></mat-datepicker-toggle>
          <mat-date-range-picker #agreementRangePicker></mat-date-range-picker>
        </mat-form-field>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button matMiniFab (click)="save()" [disabled]="!form().valid() || pending()">
        <mat-icon>download</mat-icon>
      </button>
    </mat-dialog-actions>
    @if (pending()) {
      <mat-progress-bar mode="indeterminate" />
    }
  `,
})
export class TimeExportForm {
  data = inject(MAT_DIALOG_DATA) as TimeExportData;
  hostDialog = inject(MatDialogRef);
  messenger = inject(Messenger);
  jira = inject(Jira);

  pending = signal(false);

  model = signal({
    StartDate: moment(),
    EndDate: moment(),
  });
  form = form(this.model, (schema) => {
    required(schema.StartDate);
    required(schema.EndDate);
  });

  save() {
    const { StartDate, EndDate } = this.form().value();
    this.pending.set(true);

    const data = {
      from_date: StartDate.format('Y-MM-DD'),
      till_date: EndDate.format('Y-MM-DD'),
    };

    this.jira
      .downloadCsv(this.data.id, data)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: (response) => {
          // Create blob from response and trigger browser download
          const file = new Blob([response], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = `${this.data.id}_time_report.csv`;
          link.click();
          this.hostDialog.close(true);
        },
        error: this.messenger.handleHttpError,
      });
  }
}
